import { Value } from '@helo/utils';
import { StringFilterMode, } from './filter.types';
const scalarFilterKeys = new Set([
    'equals',
    'in',
    'notIn',
    'lt',
    'lte',
    'gt',
    'gte',
    'contains',
    'startsWith',
    'endsWith',
    'not',
    'mode',
]);
const normalizeString = (value, caseInsensitive) => caseInsensitive ? value.toLowerCase() : value;
const valuesEqual = (left, right, caseInsensitive) => {
    if (typeof left !== 'string' || typeof right !== 'string') {
        return left === right;
    }
    return (normalizeString(left, caseInsensitive) ===
        normalizeString(right, caseInsensitive));
};
const normalizeComparable = (value, caseInsensitive) => typeof value === 'string' && caseInsensitive ? value.toLowerCase() : value;
const matchesFilterInput = (filterInput, inheritedMode) => (inputValue) => {
    const mode = 'mode' in filterInput
        ? (filterInput.mode ?? inheritedMode)
        : inheritedMode;
    const caseInsensitive = mode === StringFilterMode.Insensitive;
    for (const filterKey in filterInput) {
        switch (filterKey) {
            case 'equals':
                if (!valuesEqual(inputValue, filterInput.equals, caseInsensitive)) {
                    return false;
                }
                break;
            case 'in':
                if ('in' in filterInput &&
                    !filterInput.in?.some(value => valuesEqual(inputValue, value, caseInsensitive))) {
                    return false;
                }
                break;
            case 'notIn':
                if ('notIn' in filterInput &&
                    filterInput.notIn?.some(value => valuesEqual(inputValue, value, caseInsensitive))) {
                    return false;
                }
                break;
            case 'lt':
                if ('lt' in filterInput &&
                    !Value.isNullable(filterInput.lt) &&
                    normalizeComparable(inputValue, caseInsensitive) >=
                        normalizeComparable(filterInput.lt, caseInsensitive)) {
                    return false;
                }
                break;
            case 'lte':
                if ('lte' in filterInput &&
                    !Value.isNullable(filterInput.lte) &&
                    normalizeComparable(inputValue, caseInsensitive) >
                        normalizeComparable(filterInput.lte, caseInsensitive)) {
                    return false;
                }
                break;
            case 'gt':
                if ('gt' in filterInput &&
                    !Value.isNullable(filterInput.gt) &&
                    normalizeComparable(inputValue, caseInsensitive) <=
                        normalizeComparable(filterInput.gt, caseInsensitive)) {
                    return false;
                }
                break;
            case 'gte':
                if ('gte' in filterInput &&
                    !Value.isNullable(filterInput.gte) &&
                    normalizeComparable(inputValue, caseInsensitive) <
                        normalizeComparable(filterInput.gte, caseInsensitive)) {
                    return false;
                }
                break;
            case 'contains':
                if ('contains' in filterInput &&
                    !Value.isNullable(filterInput.contains) &&
                    (typeof inputValue !== 'string' ||
                        !normalizeString(inputValue, caseInsensitive).includes(normalizeString(filterInput.contains, caseInsensitive)))) {
                    return false;
                }
                break;
            case 'startsWith':
                if ('startsWith' in filterInput &&
                    !Value.isNullable(filterInput.startsWith) &&
                    (typeof inputValue !== 'string' ||
                        !normalizeString(inputValue, caseInsensitive).startsWith(normalizeString(filterInput.startsWith, caseInsensitive)))) {
                    return false;
                }
                break;
            case 'endsWith':
                if ('endsWith' in filterInput &&
                    !Value.isNullable(filterInput.endsWith) &&
                    (typeof inputValue !== 'string' ||
                        !normalizeString(inputValue, caseInsensitive).endsWith(normalizeString(filterInput.endsWith, caseInsensitive)))) {
                    return false;
                }
                break;
            case 'not':
                if ('not' in filterInput &&
                    !Value.isNullable(filterInput.not) &&
                    matchesFilterInput(filterInput.not, mode)(inputValue)) {
                    return false;
                }
                break;
            case 'mode':
                break;
        }
    }
    return true;
};
const isFilterMatch = (objectFilter) => (inputObject) => {
    if (!inputObject ||
        typeof inputObject !== 'object' ||
        Array.isArray(inputObject)) {
        return false;
    }
    for (const key of Object.keys(objectFilter)) {
        const filterInput = objectFilter[key];
        if (filterInput == null)
            continue;
        if (key === 'AND' || key === 'OR' || key === 'NOT' || key === 'not') {
            const clauses = Array.isArray(filterInput) ? filterInput : [filterInput];
            const matches = (clause) => isFilterMatch(clause)(inputObject);
            const result = key === 'OR'
                ? clauses.some(clause => matches(clause))
                : key === 'AND'
                    ? clauses.every(clause => matches(clause))
                    : clauses.every(clause => !matches(clause));
            if (!result)
                return false;
            continue;
        }
        const inputValue = inputObject[key];
        if (Array.isArray(inputValue)) {
            if (!Object.keys(filterInput).every(filterKey => filterKey === 'some' ||
                filterKey === 'every' ||
                filterKey === 'none'))
                return false;
            const relationFilter = filterInput;
            for (const operator of ['some', 'every', 'none']) {
                const clause = relationFilter[operator];
                if (clause == null)
                    continue;
                const matches = isFilterMatch(clause);
                const result = operator === 'some'
                    ? inputValue.some(matches)
                    : operator === 'every'
                        ? inputValue.every(matches)
                        : !inputValue.some(matches);
                if (!result)
                    return false;
            }
        }
        else if (inputValue != null && typeof inputValue === 'object') {
            if (!isFilterMatch(filterInput)(inputValue))
                return false;
        }
        else {
            if (!Object.keys(filterInput).every(filterKey => scalarFilterKeys.has(filterKey))) {
                return false;
            }
            if (!matchesFilterInput(filterInput)(inputValue))
                return false;
        }
    }
    return true;
};
export const Filter = {
    isFilterMatch,
};
//# sourceMappingURL=filter.js.map